import { useMemo } from 'react';
import { cssVar, Button } from '@cad-arc';
import { some } from 'lodash';
import { observer } from 'mobx-react-lite';
import { useFormContext, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import {
    ConsolidatedLocationView,
    EventPanelFormType,
    EventPanelMetadata,
    ClearEventFormType,
} from '@mark43/cad-api';
import { FrPageFooter } from '../fr-page-layout/fr-page-footer';
import { useBootstrapState } from '../bootstrap/bootstrap-provider';
import {
    FR_UNASSIGN_CURRENT_USER_UNIT_SUB_PATH,
    FR_CLEAR_EVENT_SUB_PATH,
    FR_EVENT_ROOT_PATH,
} from '../router/constants';
import { useEventApi } from '../event-panel/event-api/use-event-api';
import { useEventMetadataStore } from '../event-panel/event-panel-form/event-metadata';
import { useUnitAssignment } from '../unit-assignment/unit-assignment-context';
import { useOpenFrEventDetails } from '../fr-event-details-page/hooks/use-open-fr-event-details';
import { FrAssignButton } from './fr-assign-button';
import { FR_EVENT_DETAILS_TABS } from '../fr-event-details-page/constants';
import { useNavDrawers } from '../fr-navigation/components/use-nav-drawer';
import { useAgencyTypeGlobalAttributeId } from '../common/hooks/use-agency-type-global-attribute-id';
import { useGetAgencyType } from '../common/hooks/use-get-agency-type';
import { ReactivateEventButton } from '../common/components/reactivate-event-button';
import { GenerateRenButton } from '../common/components/generate-ren-button';
import { getEventDataForSelfAssignmentModal } from './helpers';
import { getFullLocationAddressForDisplay } from '../location/helpers';
import { useIntegratedForm } from '../forms/use-integrated-form';
import { useUnassignSelfMutation } from '../dispositions/hooks/use-unassign-self-mutation';
import { useClearEventMutation } from '../dispositions/hooks/use-clear-event-mutation';
import { EventExportButton } from '../event-panel/event-panel-footer/event-export-button';

function checkIfUserCanClearEvent({
    isClearedEvent,
    isAssignedEvent,
    isCurrentUserUnitAssignedToEvent,
    userHasClearEventAbility,
    unitLevelDispositionsEnabled,
    isPrimaryUnit,
    isCurrentUserUnitLastAssignedUnit,
}: {
    isClearedEvent: boolean;
    isAssignedEvent: boolean;
    isCurrentUserUnitAssignedToEvent: boolean;
    userHasClearEventAbility: boolean;
    unitLevelDispositionsEnabled: boolean;
    isPrimaryUnit: boolean;
    isCurrentUserUnitLastAssignedUnit: boolean;
}) {
    if (
        isClearedEvent ||
        !isAssignedEvent ||
        !isCurrentUserUnitAssignedToEvent ||
        !userHasClearEventAbility
    ) {
        return false;
    }
    if (!unitLevelDispositionsEnabled) {
        return true;
    }
    return isPrimaryUnit || isCurrentUserUnitLastAssignedUnit;
}

export const FREventPageFooter = observer(function FREventPageFooter({
    hideMoreDetailsBtn,
}: {
    hideMoreDetailsBtn?: boolean;
}) {
    const agencyTypeGlobalAttrId = useAgencyTypeGlobalAttributeId();
    const getAgencyType = useGetAgencyType(agencyTypeGlobalAttrId);
    const { toggleDrawer: toggleSubjectDataDrawer } = useNavDrawers('fr-event-subject-data');
    const unitAssignment = useUnitAssignment();
    const openFrEventDetails = useOpenFrEventDetails();
    const eventMetadataStore = useEventMetadataStore();
    const eventForm = useFormContext<EventPanelFormType>();
    const navigate = useNavigate();
    const { eventPanelOptions } = useBootstrapState();
    const {
        clearEvent: userHasClearEventAbility,
        assignSelfToEvents,
        canReactivateEvent,
        subjectDataEnabled,
        unitLevelDispositionsEnabled,
        additionalDispositionsEnabled,
    } = useEnabledFeatures();

    const {
        pillUnits,
        eventId: currentEventId,
        agencyId,
        isScheduledEvent,
        isActive,
        agencyEventNumber,
    } = eventMetadataStore?.state?.metadata ?? ({} as EventPanelMetadata);
    const isClearedEvent = !!currentEventId && !isActive && !isScheduledEvent;
    const assignedEventId = unitAssignment?.assignedEvent?.eventId;
    const assignedEventNumber = unitAssignment?.assignedEvent?.eventNumber;
    const isAssignedEvent = unitAssignment?.assignedEvent?.eventId === currentEventId;
    const isPrimaryUnit = !!unitAssignment?.isPrimary;

    const assignedEventMetadataStore = useEventMetadataStore();
    const assignedEventForm = useIntegratedForm<EventPanelFormType>().form;
    const assignedEventPillUnits = assignedEventMetadataStore.state.metadata?.pillUnits;

    const hasAssignedUnits = !!pillUnits?.length;
    const isCurrentUserUnitAssignedToEvent =
        hasAssignedUnits && unitAssignment?.unitId
            ? some(pillUnits, { id: unitAssignment?.unitId })
            : false;
    const isCurrentUserUnitLastAssignedUnit =
        isCurrentUserUnitAssignedToEvent && pillUnits?.length === 1;

    const showClearEventButton = checkIfUserCanClearEvent({
        unitLevelDispositionsEnabled,
        isPrimaryUnit,
        isCurrentUserUnitAssignedToEvent,
        userHasClearEventAbility,
        isClearedEvent,
        isAssignedEvent,
        isCurrentUserUnitLastAssignedUnit,
    });

    const showAssignButton =
        hideMoreDetailsBtn &&
        currentEventId &&
        !!unitAssignment &&
        assignSelfToEvents &&
        !isCurrentUserUnitAssignedToEvent &&
        !isClearedEvent;

    const eventLocation = useWatch({
        control: eventForm.control,
        name: 'location',
    }) as ConsolidatedLocationView;

    const assignedEventLocation = useWatch({
        control: assignedEventForm.control,
        name: 'location',
    }) as ConsolidatedLocationView;

    const selectedEvent = getEventDataForSelfAssignmentModal({
        eventId: currentEventId,
        eventNumber: agencyEventNumber,
        eventLocation: eventLocation && getFullLocationAddressForDisplay(eventLocation),
        assignedUnits: pillUnits,
    });

    const assignedEvent = getEventDataForSelfAssignmentModal({
        eventId: assignedEventId,
        eventNumber: assignedEventNumber,
        eventLocation:
            assignedEventLocation && getFullLocationAddressForDisplay(assignedEventLocation),
        assignedUnits: assignedEventPillUnits,
    });

    const handleMoreDetailsBtn = () => {
        if (assignedEventId) {
            openFrEventDetails(assignedEventId, FR_EVENT_DETAILS_TABS.RELATED_DATA);
        }
    };

    const isClearEventButtonEnabled =
        assignedEventId &&
        currentEventId !== undefined &&
        agencyId !== undefined &&
        eventPanelOptions !== undefined;

    const unassignSelfMutation = useUnassignSelfMutation({
        onMutationSuccess: () => {
            openFrEventDetails(currentEventId, FR_EVENT_DETAILS_TABS.NARRATIVE);
        },
    });
    const clearEventMutation = useClearEventMutation({
        onMutationSuccess: () => {
            openFrEventDetails(currentEventId, FR_EVENT_DETAILS_TABS.NARRATIVE);
        },
    });

    const shouldSkipDispositionForm =
        unitLevelDispositionsEnabled && !additionalDispositionsEnabled && !isPrimaryUnit;
    const { clearEvent, unassignCurrentUserUnit } = useEventApi({
        eventMetadataStore,
        eventForm,
        onClearEvent() {
            if (isClearEventButtonEnabled) {
                if (shouldSkipDispositionForm) {
                    clearEventMutation.mutate({
                        eventId: currentEventId,
                    } as ClearEventFormType);
                } else {
                    navigate(`${FR_EVENT_ROOT_PATH}/${assignedEventId}/${FR_CLEAR_EVENT_SUB_PATH}`);
                }
            }
        },
        onUnassignCurrentUserUnit() {
            if (shouldSkipDispositionForm) {
                unassignSelfMutation.mutate(unitAssignment?.unitId ? [unitAssignment.unitId] : []);
            } else {
                navigate(
                    `${FR_EVENT_ROOT_PATH}/${assignedEventId}/${FR_UNASSIGN_CURRENT_USER_UNIT_SUB_PATH}`
                );
            }
        },
    });

    const showSubjectDataButton = useMemo(() => {
        return subjectDataEnabled && isAssignedEvent && getAgencyType() === 'police';
    }, [getAgencyType, isAssignedEvent, subjectDataEnabled]);

    return (
        <FrPageFooter
            customStyles={{ justifyContent: 'flex-end', zIndex: cssVar('arc.zIndices.banner') }}
        >
            {showAssignButton && (
                <FrAssignButton
                    style={{ marginRight: 'auto' }}
                    size="lg"
                    hasAssignedUnits={hasAssignedUnits}
                    selectedEvent={selectedEvent}
                    assignedEvent={assignedEvent}
                />
            )}
            {!hideMoreDetailsBtn && (
                <Button style={{ marginRight: 'auto' }} onClick={handleMoreDetailsBtn}>
                    More details
                </Button>
            )}
            <GenerateRenButton eventId={currentEventId} />
            {currentEventId && agencyEventNumber && (
                <EventExportButton eventId={currentEventId} eventNumber={agencyEventNumber} />
            )}
            {showSubjectDataButton && (
                <Button onClick={toggleSubjectDataDrawer}>Subject Data</Button>
            )}
            {unitLevelDispositionsEnabled && isAssignedEvent && (
                <Button onClick={unassignCurrentUserUnit}>Unassign Myself</Button>
            )}
            {showClearEventButton && (
                <Button disabled={!isClearEventButtonEnabled} onClick={clearEvent}>
                    Clear Event
                </Button>
            )}
            {isClearedEvent && canReactivateEvent && (
                <ReactivateEventButton eventId={currentEventId} />
            )}
        </FrPageFooter>
    );
});
